/**
 * CreateCampaignModal Component
 * Modal for creating or editing a campaign with a form matching the BE API
 */

import { useState, useEffect } from 'react'
import { X, Megaphone } from 'lucide-react'
import { Campaign } from '../../types'
import { AdvertiserSelect } from '../../components/shared/AdvertiserSelect'
import { Advertiser } from '../../lib/api/advertisers'

interface CreateCampaignModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { name: string; description?: string; advertiser_id?: number }) => Promise<void>
  isLoading?: boolean
  campaign?: Campaign | null  // If provided, modal is in edit mode
}

export function CreateCampaignModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  campaign = null
}: CreateCampaignModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedAdvertiser, setSelectedAdvertiser] = useState<Advertiser | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isEditMode = !!campaign

  // Pre-fill form when editing
  useEffect(() => {
    if (campaign) {
      setName(campaign.name || '')
      setDescription(campaign.description || '')
      
      // Pre-fill advertiser from campaign data (API now includes full advertiser object)
      if (campaign.advertiser) {
        setSelectedAdvertiser(campaign.advertiser as Advertiser)
      } else {
        setSelectedAdvertiser(null)
      }
    } else {
      resetForm()
    }
  }, [campaign, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate advertiser (required)
    if (!selectedAdvertiser) {
      setError('Please select an advertiser')
      return
    }

    // Validate name
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError('Campaign name is required')
      return
    }

    if (trimmedName.length > 128) {
      setError('Campaign name must be 128 characters or less')
      return
    }

    // Validate description length
    const trimmedDescription = description.trim()
    if (trimmedDescription.length > 1000) {
      setError('Description must be 1000 characters or less')
      return
    }

    try {
      await onSubmit({
        name: trimmedName,
        description: trimmedDescription || undefined,
        advertiser_id: selectedAdvertiser?.id
      })
      // Reset form on success
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} campaign`)
    }
  }

  const resetForm = () => {
    setName('')
    setDescription('')
    setSelectedAdvertiser(null)
    setError(null)
  }

  const handleClose = () => {
    if (!isLoading) {
      resetForm()
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white dark:bg-gray-950 rounded-2xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-900/40 dark:via-gray-950 dark:to-gray-950">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase flex items-center gap-1.5">
                <Megaphone className="h-3 w-3" />
                Campaign
              </div>
              <h2 className="text-2xl font-bold mt-1">
                {isEditMode ? 'Edit campaign' : 'Create campaign'}
              </h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {isEditMode
                  ? 'Update the campaign details and associated advertiser.'
                  : 'Set up a new campaign by giving it a name and selecting an advertiser.'}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="p-2 rounded-full bg-white dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 shadow-sm"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Advertiser Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Advertiser <span className="text-red-500">*</span>
            </label>
            <AdvertiserSelect
              value={selectedAdvertiser}
              onChange={setSelectedAdvertiser}
              placeholder="Search and select an advertiser..."
              disabled={isLoading}
            />
          </div>

          {/* Campaign Name */}
          <div>
            <label
              htmlFor="campaign-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Campaign Name <span className="text-red-500">*</span>
            </label>
            <input
              id="campaign-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter campaign name"
              disabled={isLoading}
              maxLength={128}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {name.length}/128 characters
            </p>
          </div>

          {/* Campaign Description */}
          <div>
            <label
              htmlFor="campaign-description"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Description <span className="text-gray-400">(Optional)</span>
            </label>
            <textarea
              id="campaign-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the campaign..."
              disabled={isLoading}
              rows={4}
              maxLength={1000}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed resize-none"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {description.length}/1000 characters
            </p>
          </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name.trim() || !selectedAdvertiser}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {isLoading
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : (isEditMode ? 'Save Changes' : 'Create Campaign')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
